import {ApiError, Checkout, ICartPosition, PaymentFactory} from "@connox/typescript-library";
import * as React from "react";
import * as Sentry from "@sentry/browser";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useCartContext} from "../../../cart/CartContextProvider/CartContextProvider";
import CartDetails from "../../../cart/CartDetails/CartDetails";
import ItemList from "../../../cart/ItemList/ItemList";
import VoucherBlock from "../../../cart/Rebates/VoucherBlock";
import {Button, ButtonStyles, ButtonType} from "../../../lib/form/Button/Button";
import {getShopId} from "../../../lib/getShopId";
import {amazonMerchantId, isAmazonLoggedIn} from "../../../lib/payments/AmazonPayLibrary";
import {IPaymentModule} from "../../../lib/payments/IPaymentModule";
import {PaymentController, Payments} from "../../../lib/payments/PaymentController";
import "../../../lib/SimpleRadioList.scss";
import window, {IAmazonError} from "../../../lib/WindowExtended";
import LineLoader from "../../../utils/loader/LineLoader";
import ListLoader from "../../../utils/loader/ListLoader";
import Spinner from "../../../utils/loader/Spinner";
import {CheckoutError, CheckoutStep, DEFAULT_ERROR_MESSAGE, IIsVisible} from "../../Checkout";
import {useCheckoutContext} from "../../CheckoutContextProvider/CheckoutContextProvider";
import {
  amazonState,
  isSuccessfulAmazonCheckout,
  useCheckoutViewContext,
} from "../../CheckoutContextProvider/CheckoutViewContextProvider";
import AnnotationBlock from "./AnnotationBlock";
import "./Summary.scss";
import SummaryAddress from "./SummaryAddress";
import SummaryNewsletter from "./SummaryNewsletter";
import SummaryPaymentOption from "./SummaryPaymentOption";
import SummaryShippingOption from "./SummaryShippingOption";

const Summary: React.FC<IIsVisible> = (props) => {
  const {setIsIntoCheckout} = useCartContext();
  const {checkout} = useCheckoutContext();
  const checkoutView = useCheckoutViewContext();
  const {cartData, paymentId} = checkout;
  const [paymentModule, setPaymentModule] = useState<IPaymentModule | null>(
    checkout.paymentId ? PaymentController.createPayment(checkout, checkout.paymentId) : null,
  );
  const {t} = useTranslation("checkout");
  const itemPosCount = cartData.positions.filter((pos: ICartPosition) => pos.type === "Item" && !!pos.item).length;
  const buttonText = t("Jetzt kaufen");
  const amazonPayError = "AMAZON_PAY_ERROR";
  const btoaError = btoa(amazonPayError);
  const [errors, setErrors] = useState<string[]>([]);
  const restrictingPayments = [Payments.AmazonPay, Payments.PaypalExpressBraintree, Payments.CreditCardBraintree];
  const isCartManipulationAllowed = paymentId && !restrictingPayments.includes(paymentId);

  /** set payment module according to payment id if set */
  useEffect(() => {
    if (paymentId === Payments.PaypalExpressBraintree || paymentId === Payments.AmazonPay) {
      return;
    }
    if (paymentId) {
      setPaymentModule(PaymentController.createPayment(checkout, paymentId));
    }
  }, [paymentId, props.isVisible]);

  /** do initial things when view is visible and payment module is set */
  useEffect(() => {
    if (props.isVisible) {
      setErrors([]);
    }

    if (props.isVisible && paymentModule && paymentId) {
      try {
        void paymentModule.onEnterSummary();
      } catch (err) {
        captureSentryException(err, checkout, "onEnterSummary");
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment
        setErrors([t("Etwas ist schiefgegangen: {{message}}", {message: err.originalError?.message || err.message})]);
      }
    }
  }, [paymentModule, props.isVisible]);

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    if (
      urlSearchParams.get("AuthenticationStatus") === "Success" &&
      !window.amazonPayAuthorized &&
      cartData.positions.find((pos) => pos.reference && pos.reference.itemId !== null) &&
      checkout.messages.find((msg) => msg.code === "PAYMENT_NOT_AUTHORIZED")
    ) {
      window.amazonPayAuthorized = true;
      if (!checkout.paymentTransaction) {
        captureSentryException(
          new Error("No paymentTransaction but AuthenticationStatus=Success"),
          checkout,
          "Amazon Pay Purchase after redirect",
          {checkoutErrors: checkout.messages},
        );
        setErrors([t("Beim initialisieren von Amazon Pay ist etwas schiefgelaufen")]);

        return;
      }
      PaymentFactory.authorizePayment(checkout.paymentTransaction.paymentTransactionId)
        .then(async () => {
          await checkout.purchase();
        })
        .catch((err) => {
          captureSentryException(err, checkout, "APay Authorize Payment (after redirect)");

          setErrors(() => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
            const errorMessage = err.originalError?.message || err.message;

            return [t(errorMessage, t(DEFAULT_ERROR_MESSAGE))];
          });
        });
    } else if (urlSearchParams.get("error") === btoaError) {
      captureSentryException(
        new Error("APay initConfirmationFlow callback error (after redirect)"),
        checkout,
        "useEffect handling urlparams redirecting",
      );
      setErrors(t(amazonPayError));
    }
  }, []);

  // region insanely large button logic

  const ExtButton: React.FC<{submitted: boolean; onPurchase: () => void}> = (extButtonProps) => (
    <Button
      action={extButtonProps.onPurchase}
      styles={[ButtonStyles.wide]}
      type={extButtonProps.submitted ? ButtonType.disabled : ButtonType.default}
    >
      {extButtonProps.submitted ? <Spinner /> : buttonText}
    </Button>
  );

  const purchaseButtonErrors = (err: ApiError) => {
    const sections = document.querySelectorAll("#ModalRootElement > section");
    if (sections.length) {
      sections.forEach((section) => (section.scrollTop = 0));
    }
    captureSentryException(err, checkout, "Summary Purchase Button");
    setErrors(() => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
      const errorMessage = err.originalError?.message || err.message;

      return [t(errorMessage, t(DEFAULT_ERROR_MESSAGE))];
    });
  };

  /* PAYPAL EXPRESS */
  const PaypalExpressSummaryButton: React.FC = () => {
    const [submitted, setSubmitted] = useState(false);
    const onPurchase = async () => {
      setSubmitted(true);
      try {
        const transaction = await PaymentFactory.initPayment(`${getShopId()}`, checkout.checkoutId);
        await PaymentFactory.authorizePayment(transaction.paymentTransactionId, window.pp_nonce);
        await checkout.purchase();
      } catch (err) {
        captureSentryException(err, checkout, "Paypal Express Purchase Button", {ppNonceExists: !!window.pp_nonce});
        setErrors(() => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
          const errorMessage = err.originalError?.message || err.message;

          return [t(errorMessage, t(DEFAULT_ERROR_MESSAGE))];
        });
      }
    };

    return <ExtButton onPurchase={onPurchase} submitted={submitted} />;
  };

  /* AMAZON PAY */
  const AmazonPayButton: React.FC = () => {
    const [submitted, setSubmitted] = useState(false);

    const onPurchase = async () => {
      setSubmitted(true);
      try {
        const transaction = await PaymentFactory.initPayment(
          `${getShopId()}`,
          checkout.checkoutId,
          window.orderReferenceId,
        );
        window.paymentTransactionId = transaction.paymentTransactionId;
        window.OffAmazonPayments.initConfirmationFlow(
          amazonMerchantId,
          window.orderReferenceId,
          (confirmationFlow): void => {
            try {
              const {origin, pathname, search} = window.location;
              const usp = new URLSearchParams(search);

              usp.set("gotoStep", CheckoutStep.SUMMARY);
              const successUrl = origin + pathname + "?" + usp.toString();
              usp.set("error", btoaError);
              const failureUrl = origin + pathname + "?" + usp.toString();

              if (!window.paymentTransactionId) {
                window.paymentTransactionId = checkout.paymentTransaction?.paymentTransactionId || "";
              }
              if (!window.paymentTransactionId) {
                throw new Error("Der Kauf konnte nicht initialisiert werden. Bitte wiederholen Sie den Kaufprozess");
              }
              void PaymentFactory.amazonPayPreAuthorize(window.paymentTransactionId, successUrl, failureUrl).then(
                () => {
                  confirmationFlow.success();
                },
              );
            } catch (callbackError) {
              setSubmitted(false);
              captureSentryException(callbackError, checkout, "Amazon Pay Purchase preauthorize (before redirect)");
              /* eslint-disable @typescript-eslint/no-unsafe-member-access */
              setErrors(() => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
                const errorMessage =
                  callbackError.originalError?.response?.data.result ||
                  callbackError.originalError?.message ||
                  callbackError.message;

                return [t(errorMessage, t(DEFAULT_ERROR_MESSAGE))];
              });
              confirmationFlow.error();
            }
          },
        );
      } catch (err) {
        setSubmitted(false);
        captureSentryException(err, checkout, "Amazon Pay Purchase initPayment / initConfirmationFlow execution");
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (err.originalError?.response?.data?.detail !== "Transaction could not be cancelled") {
          setErrors([t("Der Kauf konnte nicht initialisiert werden. Bitte wiederholen Sie den Kaufprozess")]);
        } else {
          setErrors(() => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const errorMessage = err.originalError?.response?.data.result || err.originalError?.message || err.message;

            return [t(errorMessage, t(DEFAULT_ERROR_MESSAGE))];
          });
        }
      }
    };

    return <ExtButton onPurchase={onPurchase} submitted={submitted} />;
  };

  /* DEFAULT/INTERNAL PAYMENT */
  const DefaultButton: React.FC = () =>
    paymentModule ? <paymentModule.PurchaseButton label={buttonText} handleError={purchaseButtonErrors} /> : null;

  /* FALLBACK */
  const FallbackButton: React.FC = () => <Button type={ButtonType.disabled}>{t("Keine gültige Zahlungsart")}</Button>;

  const purchaseButtonMemoized = React.useMemo(() => {
    let ButtonToReturn = FallbackButton;

    switch (true) {
      case paymentId === Payments.PaypalExpressBraintree:
        ButtonToReturn = PaypalExpressSummaryButton;
        break;

      case paymentId === Payments.AmazonPay:
        ButtonToReturn = AmazonPayButton;
        break;

      case paymentModule !== null:
        ButtonToReturn = DefaultButton;
        break;
    }

    return <ButtonToReturn />;
  }, [paymentId, paymentModule, props.isVisible]);

  // endregion

  /** visibility check */
  if (!props.isVisible) {
    return null;
  }

  if (new URLSearchParams(window.location.search).get("AuthenticationStatus") === "Success" && !errors.length) {
    if (checkout.isCompleted) {
      return null;
    } else {
      return (
        <div className="Shipping Mobile-Only-Friendly">
          <ListLoader />
          <LineLoader speed={4} linePositionY={"2"} linePositionX={"0"} lineHeight={"3"} lineWidth={"50"} />
        </div>
      );
    }
  }

  if (errors.length) {
    const sections = document.querySelectorAll("#ModalRootElement > section");
    if (sections.length) {
      sections.forEach((section) => (section.scrollTop = 0));
    }
  }

  const onAmazonWidgetError = (err: IAmazonError) => {
    captureSentryException(
      new Error(`${err.getErrorCode()}: ${err.getErrorMessage()}`),
      checkout,
      "Amazon Readable Widget",
    );

    setErrors([t(DEFAULT_ERROR_MESSAGE)]);
  };

  return (
    <div className="Summary reduced-width">
      <h2>{t("Prüfen Sie Ihre Bestellung")}</h2>
      <CheckoutError>{errors}</CheckoutError>
      <CartDetails reduced />
      <SummaryShippingOption goToShippingView={() => checkoutView.gotoStep(CheckoutStep.SHIPPING)} />
      <h2>{t("Ihr Warenkorb", {count: itemPosCount})}</h2>
      <ItemList editable={false} goToCartView={() => setIsIntoCheckout(false)} />
      <SummaryAddress
        goToAddressView={() => checkoutView.gotoStep(CheckoutStep.ADDRESS)}
        onError={onAmazonWidgetError}
      />
      <SummaryPaymentOption
        goToPaymentView={() => checkoutView.gotoStep(CheckoutStep.PAYMENT)}
        onError={onAmazonWidgetError}
      />
      {isCartManipulationAllowed && <VoucherBlock />}
      <AnnotationBlock />
      <SummaryNewsletter />
      <CartDetails />
      {purchaseButtonMemoized}
    </div>
  );
};

export default React.memo(Summary);

const captureSentryException = (
  err: Error,
  checkout: Checkout,
  place: string,
  additional?: Record<string, unknown>,
) => {
  let checkoutData = {logisticsType: checkout.logisticsTypeId, paymentType: checkout.paymentId};
  if (additional) {
    checkoutData = {...checkoutData, ...additional};
  }

  Sentry.captureException(err, {
    extra: {
      checkoutData,
      amazonInfo: {
        loggedIn: isAmazonLoggedIn(),
        urlParamsSet: isSuccessfulAmazonCheckout(),
        amazonState: amazonState(checkout.paymentId),
        referenceIdSet: !!window.orderReferenceId,
      },
      place,
    },
  });
};
